import { NavigationType } from "react-router-dom"

export type PageTransitionDirection = "forward" | "backward"

export interface HistoryDirectionInput {
  previousIndex: number | null
  nextIndex: number | null
  fallback?: PageTransitionDirection
}

export interface PageTransitionDecisionInput {
  explicitIntent?: {
    animate: boolean
    direction: PageTransitionDirection
  }
  navigationType: NavigationType
  previousHistoryIndex: number | null
  nextHistoryIndex: number | null
}

export interface PageTransitionDecision {
  animate: boolean
  direction: PageTransitionDirection
}

interface PageTransitionConfig {
  duration: string
  easing: string
}

export const PAGE_TRANSITION_TUNING = {
  pushEnter: "translate3d(30%, 0, 0)",
  popEnter: "translate3d(-12%, 0, 0)",
  /** Light parallax — old screen drifts left behind the incoming page. */
  pushExit: "translate3d(-12%, 0, 0)",
  /** Current screen slides fully off to the right on back navigation. */
  popExit: "translate3d(100%, 0, 0)",
  final: "translate3d(0, 0, 0)",
  pushEnterOpacity: 0.98,
  popEnterOpacity: 0.96,
  pushExitOpacity: 0.94,
  popExitOpacity: 1,
  duration: "0.32s",
  popDuration: "0.32s",
  easing: "cubic-bezier(0.32, 0.72, 0, 1)",
  /** Slightly decelerated curve — exit feels less abrupt than enter. */
  exitEasing: "cubic-bezier(0.22, 0.61, 0.36, 1)",
  reducedMotionDuration: "0.01s",
  background: "var(--be-page-transition-background, #f2f3f4)",
  /** Stacking order while a transition is active. */
  layerAbove: "10",
  layerBelow: "1",
} as const

export interface PageMotionKeyframes {
  from: string
  to: string
  opacityFrom: number
  opacityTo: number
}

export interface PageTransitionMotion {
  incoming: PageMotionKeyframes
  outgoing: PageMotionKeyframes
  incomingZIndex: string
  outgoingZIndex: string
}

export function getPageEnterKeyframes(
  direction: PageTransitionDirection
): PageMotionKeyframes {
  if (direction === "forward") {
    return {
      from: PAGE_TRANSITION_TUNING.pushEnter,
      to: PAGE_TRANSITION_TUNING.final,
      opacityFrom: PAGE_TRANSITION_TUNING.pushEnterOpacity,
      opacityTo: 1,
    }
  }

  return {
    from: PAGE_TRANSITION_TUNING.popEnter,
    to: PAGE_TRANSITION_TUNING.final,
    opacityFrom: PAGE_TRANSITION_TUNING.popEnterOpacity,
    opacityTo: 1,
  }
}

export function getPageExitKeyframes(
  direction: PageTransitionDirection
): PageMotionKeyframes {
  if (direction === "forward") {
    return {
      from: PAGE_TRANSITION_TUNING.final,
      to: PAGE_TRANSITION_TUNING.pushExit,
      opacityFrom: 1,
      opacityTo: PAGE_TRANSITION_TUNING.pushExitOpacity,
    }
  }

  return {
    from: PAGE_TRANSITION_TUNING.final,
    to: PAGE_TRANSITION_TUNING.popExit,
    opacityFrom: 1,
    opacityTo: PAGE_TRANSITION_TUNING.popExitOpacity,
  }
}

export function getPageTransitionMotion(
  direction: PageTransitionDirection
): PageTransitionMotion {
  const isForward = direction === "forward"

  return {
    incoming: getPageEnterKeyframes(direction),
    outgoing: getPageExitKeyframes(direction),
    incomingZIndex: isForward
      ? PAGE_TRANSITION_TUNING.layerAbove
      : PAGE_TRANSITION_TUNING.layerBelow,
    outgoingZIndex: isForward
      ? PAGE_TRANSITION_TUNING.layerBelow
      : PAGE_TRANSITION_TUNING.layerAbove,
  }
}

export function getPageTransitionDuration(
  direction: PageTransitionDirection = "forward"
): string {
  return direction === "backward"
    ? PAGE_TRANSITION_TUNING.popDuration
    : PAGE_TRANSITION_TUNING.duration
}

export function getPageTransitionConfig(
  direction: PageTransitionDirection = "forward",
  role: "incoming" | "outgoing" = "incoming"
): PageTransitionConfig {
  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    return {
      duration: PAGE_TRANSITION_TUNING.reducedMotionDuration,
      easing: "linear",
    }
  }

  return {
    duration: getPageTransitionDuration(direction),
    easing:
      role === "outgoing"
        ? PAGE_TRANSITION_TUNING.exitEasing
        : PAGE_TRANSITION_TUNING.easing,
  }
}

export function buildPageTransitionCss(
  config: PageTransitionConfig = getPageTransitionConfig()
): string {
  return `transform ${config.duration} ${config.easing}, opacity ${config.duration} ${config.easing}`
}

export function buildExitTransitionCss(
  config: PageTransitionConfig = getPageTransitionConfig("backward", "outgoing")
): string {
  return `transform ${config.duration} ${config.easing}`
}

export function getHistoryNavigationDirection({
  previousIndex,
  nextIndex,
  fallback = "backward",
}: HistoryDirectionInput): PageTransitionDirection {
  if (
    previousIndex == null ||
    nextIndex == null ||
    previousIndex === nextIndex
  ) {
    return fallback
  }

  return nextIndex < previousIndex ? "backward" : "forward"
}

export function resolvePageTransitionDecision({
  explicitIntent,
  navigationType,
  previousHistoryIndex,
  nextHistoryIndex,
}: PageTransitionDecisionInput): PageTransitionDecision {
  if (explicitIntent) {
    return explicitIntent
  }

  if (navigationType === NavigationType.Pop) {
    return {
      animate: true,
      direction: getHistoryNavigationDirection({
        previousIndex: previousHistoryIndex,
        nextIndex: nextHistoryIndex,
        fallback: "backward",
      }),
    }
  }

  return {
    animate: false,
    direction: navigationType === NavigationType.Push ? "forward" : "backward",
  }
}
